/**
 * Athena Presentation Design System — Color Tokens
 *
 * Single source of truth for all presentation colors.
 * Do NOT use raw hex values in showcase components.
 * Import from here instead.
 *
 * Canonical base: Recipe/Study solid dark card language.
 * New showcases should match this quality level.
 */

// ── Base dark theme ──────────────────────────────────────────────────────────
export const base = {
  // Backgrounds
  bg:           '#080e1c',
  card:         '#0c1220',
  cardHero:     '#0b1524',
  cardHeroAlt:  '#0d1a2e',
  cardBible:    '#0d1325',
  cardPlan:     '#081810',
  cardPlanAlt:  '#0b1a14',
  overlay:      'rgba(0,0,0,0.18)',

  // Borders
  border:       '#1a2240',
  borderBible:  '#1e2a44',
  borderSoft:   'rgba(30,50,80,0.5)',
  borderFaint:  'rgba(255,255,255,0.04)',

  // Text
  text:         '#e8eaf2',
  textSec:      'rgba(220,234,255,0.78)',
  textMuted:    'rgba(150,170,220,0.60)',
  textFaint:    'rgba(100,130,180,0.42)',

  // Gradients
  heroGrad:     'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
  heroGradPlan: 'linear-gradient(135deg, #081810 0%, #0b1a14 100%)',
  heroGradBible:'linear-gradient(135deg, #090d1c 0%, #0b1228 100%)',
  tipGrad:      'linear-gradient(135deg, rgba(245,158,11,0.13) 0%, rgba(245,158,11,0.05) 100%)',
  avatarGrad:   'radial-gradient(circle at 35% 35%, rgba(139,92,246,0.85), rgba(59,130,246,0.65))',
};

// ── Semantic colors ──────────────────────────────────────────────────────────
export const semantic = {
  success:      '#22c55e',
  successSoft:  'rgba(34,197,94,0.12)',
  successBdr:   'rgba(34,197,94,0.22)',

  warning:      '#f59e0b',
  warningSoft:  'rgba(245,158,11,0.09)',
  warningBdr:   'rgba(245,158,11,0.25)',

  danger:       '#ef4444',
  dangerSoft:   'rgba(239,68,68,0.10)',
  dangerBdr:    'rgba(239,68,68,0.22)',

  info:         '#60a5fa',
  infoSoft:     'rgba(96,165,250,0.10)',
  infoBdr:      'rgba(96,165,250,0.22)',
};

// ── Accent palettes (per presentation category) ───────────────────────────
export const accents = {
  blue: {
    primary:  '#3B82F6',
    light:    '#60a5fa',
    soft:     'rgba(59,130,246,0.12)',
    bdr:      'rgba(59,130,246,0.22)',
    hover:    'rgba(59,130,246,0.08)',
    hoverBdr: 'rgba(59,130,246,0.35)',
  },
  indigo: {
    primary:  '#6366f1',
    light:    '#818cf8',
    soft:     'rgba(99,102,241,0.12)',
    bdr:      'rgba(99,102,241,0.25)',
    hover:    'rgba(99,102,241,0.08)',
  },
  purple: {
    primary:  '#a78bfa',
    light:    '#c4b5fd',
    soft:     'rgba(167,139,250,0.11)',
    bdr:      'rgba(167,139,250,0.22)',
    hover:    'rgba(167,139,250,0.08)',
  },
  green: {
    primary:  '#34d399',
    light:    '#6ee7b7',
    soft:     'rgba(52,211,153,0.10)',
    bdr:      'rgba(52,211,153,0.22)',
  },
  amber: {
    primary:  '#f59e0b',
    light:    '#fbbf24',
    soft:     'rgba(245,158,11,0.09)',
    bdr:      'rgba(245,158,11,0.25)',
  },
  gold: {
    primary:  '#f59e0b',
    light:    '#fbbf24',
    soft:     'rgba(245,158,11,0.09)',
    bdr:      'rgba(245,158,11,0.27)',
  },
  cyan: {
    primary:  '#22d3ee',
    light:    '#67e8f9',
    soft:     'rgba(34,211,238,0.10)',
    bdr:      'rgba(34,211,238,0.22)',
  },
  sky: {
    primary:  '#38bdf8',
    light:    '#7dd3fc',
    soft:     'rgba(56,189,248,0.10)',
    bdr:      'rgba(56,189,248,0.22)',
  },
  teal: {
    primary:  '#2dd4bf',
    soft:     'rgba(45,212,191,0.10)',
    bdr:      'rgba(45,212,191,0.22)',
  },
  rose: {
    primary:  '#f43f5e',
    soft:     'rgba(244,63,94,0.10)',
    bdr:      'rgba(244,63,94,0.22)',
  },
  orange: {
    primary:  '#fb923c',
    soft:     'rgba(251,146,60,0.10)',
    bdr:      'rgba(251,146,60,0.22)',
  },
  lime: {
    primary:  '#84cc16',
    soft:     'rgba(132,204,22,0.10)',
    bdr:      'rgba(132,204,22,0.22)',
  },
  violet: {
    primary:  '#a78bfa',
    soft:     'rgba(167,139,250,0.10)',
    bdr:      'rgba(167,139,250,0.22)',
  },
};

// ── Category theme map ────────────────────────────────────────────────────────
export const themes = {
  recipe:      accents.blue,
  study:       accents.blue,
  bible_study: accents.purple,
  plan:        accents.green,
  checklist:   accents.sky,
  report:      accents.amber,
  comparison:  accents.violet,
  tutorial:    accents.cyan,
  analysis:    { primary: '#818cf8', soft: 'rgba(129,140,248,0.10)', bdr: 'rgba(129,140,248,0.22)' },
};

export const colors = { base, semantic, accents, themes };
export default colors;
